<template>
    <div class="audit-detail-container">
        <div class="page-header">
            <el-button @click="goBack">返回</el-button>
            <h2 class="page-title">车辆审核详情</h2>
        </div>

        <div class="vehicle-info-card">
            <div class="card-header">
                <h3 class="card-title">车辆信息</h3>
                <div class="card-actions">
                    <el-button v-if="!isVehicleEditing" type="text" @click="toggleVehicleEdit" :disabled="vehicleInfo.status === '2'">编辑</el-button>
                    <template v-else>
                        <el-button type="text" @click="cancelVehicleEdit">取消</el-button>
                        <el-button type="primary" size="small" @click="saveVehicleInfo">保存</el-button>
                    </template>
                </div>
            </div>
            <div v-if="!isVehicleEditing" class="info-grid">
                <div class="info-item">
                    <label>车主姓名</label>
                    <span>{{ vehicleInfo.userName || '-' }}</span>
                </div>
                <div class="info-item">
                    <label>车牌号</label>
                    <span>{{ vehicleInfo.plateNumber || '-' }}</span>
                </div>
                <div class="info-item">
                    <label>车辆类型</label>
                    <span>{{ vehicleInfo.vehicleType || '-' }}</span>
                </div>
                <div class="info-item">
                    <label>使用性质</label>
                    <span>{{ vehicleInfo.useNature || '-' }}</span>
                </div>
                <div class="info-item">
                    <label>注册日期</label>
                    <span>{{ vehicleInfo.registerDate || '-' }}</span>
                </div>
                <div class="info-item">
                    <label>车辆状态</label>
                    <el-tag :type="getStatusTagType(vehicleInfo.status)">
                        {{ vehicleInfo.statusText }}
                    </el-tag>
                </div>
                <div class="info-item">
                    <label>检验周期</label>
                    <span>{{ vehicleInfo.checkCycle || '-' }}个月</span>
                </div>
                <div class="info-item full-width">
                    <label>行驶证图片</label>
                    <div v-if="vehicleInfo.licenseImage" class="license-image">
                        <img :src="getLicenseImageUrl(vehicleInfo.licenseImage)" alt="行驶证" @click="previewLicenseImage" />
                    </div>
                    <span v-else style="color: #999;">暂无图片</span>
                </div>
            </div>
            <div v-else class="form-grid">
                <el-form-item label="车主姓名">
                    <el-input v-model="vehicleForm.userName" disabled></el-input>
                </el-form-item>
                <el-form-item label="车牌号">
                    <el-input v-model="vehicleForm.plateNumber"></el-input>
                </el-form-item>
                <el-form-item label="车辆类型">
                    <el-input v-model="vehicleForm.vehicleType"></el-input>
                </el-form-item>
                <el-form-item label="使用性质">
                    <el-input v-model="vehicleForm.useNature"></el-input>
                </el-form-item>
                <el-form-item label="注册日期">
                    <el-date-picker v-model="vehicleForm.registerDate" type="date" value-format="yyyy-MM-dd"></el-date-picker>
                </el-form-item>
                <el-form-item label="车辆状态">
                    <el-select v-model="vehicleForm.status">
                        <el-option label="审核中" value="1"></el-option>
                        <el-option label="已绑定" value="2"></el-option>
                        <el-option label="审核失败" value="3"></el-option>
                    </el-select>
                </el-form-item>
                <el-form-item label="检验周期">
                    <el-input v-model="vehicleForm.checkCycle" disabled></el-input>
                </el-form-item>
                <el-form-item label="行驶证图片" class="full-width">
                    <div v-if="vehicleForm.licenseImage" class="license-image-edit">
                        <img :src="getLicenseImageUrl(vehicleForm.licenseImage)" alt="行驶证" style="max-width: 200px; max-height: 150px;" />
                        <el-button type="text" size="small" @click="vehicleForm.licenseImage = ''">移除图片</el-button>
                    </div>
                    <span v-else style="color: #999;">暂无图片</span>
                </el-form-item>
            </div>
        </div>

        <div class="audit-action-card">
            <h3 class="card-title">审核操作</h3>
            <div class="action-buttons">
                <el-button type="success" @click="handleAuditPass">审核通过</el-button>
                <el-button type="danger" @click="openAuditFailDialog">审核失败</el-button>
            </div>
        </div>

        <el-dialog title="审核失败" v-model="auditFailDialogVisible" width="400px">
            <el-form-item label="失败原因">
                <el-input v-model="auditForm.remark" type="textarea" :rows="3" placeholder="请输入审核失败原因"></el-input>
            </el-form-item>
            <template #footer>
                <el-button @click="auditFailDialogVisible = false">取消</el-button>
                <el-button type="danger" @click="confirmAuditFail">确认</el-button>
            </template>
        </el-dialog>

        <div class="check-info-card">
            <div class="card-header">
                <h3 class="card-title">车辆年检信息</h3>
                <el-button type="text" @click="toggleCheckEdit" :disabled="!isCheckEditing && vehicleInfo.status === '2'">{{ isCheckEditing ? '取消' : '编辑' }}</el-button>
            </div>
            <div v-if="!isCheckEditing" class="info-grid">
                <div class="info-item">
                    <label>检验到期日</label>
                    <span>{{ formatDate(checkInfo.checkExpireDate) || '-' }}</span>
                </div>
                <div class="info-item">
                    <label>检车类型</label>
                    <span>{{ checkInfo.checkType || '-' }}</span>
                </div>
                <div class="info-item">
                    <label>订单号</label>
                    <span>{{ checkInfo.orderId || '-' }}</span>
                </div>
                <div class="info-item">
                    <label>窗口号</label>
                    <span>{{ checkInfo.windowNo || '-' }}</span>
                </div>
                <div class="info-item full-width">
                    <label>备注</label>
                    <span>{{ checkInfo.remark || '-' }}</span>
                </div>
            </div>
            <div v-else class="form-grid">
                <el-form-item label="检验到期日">
                    <el-date-picker v-model="checkForm.checkExpireDate" type="date" value-format="yyyy-MM-dd"></el-date-picker>
                </el-form-item>
                <el-form-item label="检车类型">
                    <el-select v-model="checkForm.checkType">
                        <el-option label="年检" value="年检"></el-option>
                        <el-option label="免检" value="免检"></el-option>
                    </el-select>
                </el-form-item>
                <el-form-item label="检车状态">
                    <el-select v-model="checkForm.checkStatus" placeholder="非必填">
                        <el-option label="已预约" value="0"></el-option>
                        <el-option label="已到站" value="1"></el-option>
                        <el-option label="外检中" value="2"></el-option>
                        <el-option label="环检中" value="3"></el-option>
                        <el-option label="安检中" value="4"></el-option>
                        <el-option label="已取证" value="5"></el-option>
                        <el-option label="已年检" value="6"></el-option>
                        <el-option label="取消预约" value="7"></el-option>
                    </el-select>
                </el-form-item>
                <el-form-item label="订单号">
                    <el-input v-model="checkForm.orderId"></el-input>
                </el-form-item>
                <el-form-item label="窗口号">
                    <el-input v-model="checkForm.windowNo"></el-input>
                </el-form-item>
                <el-form-item label="备注" class="full-width">
                    <el-input v-model="checkForm.remark" type="textarea" :rows="2"></el-input>
                </el-form-item>
                <div class="form-actions">
                    <el-button type="primary" @click="saveCheckInfo">保存</el-button>
                </div>
            </div>
        </div>

        <div class="insurance-info-card">
            <div class="card-header">
                <h3 class="card-title">车辆保险信息</h3>
                <el-button type="text" @click="toggleInsuranceEdit" :disabled="!isInsuranceEditing && vehicleInfo.status === '2'">{{ isInsuranceEditing ? '取消' : '编辑' }}</el-button>
            </div>
            <div v-if="!isInsuranceEditing" class="info-grid">
                <div class="info-item">
                    <label>保险公司</label>
                    <span>{{ insuranceInfo.company || '-' }}</span>
                </div>
                <div class="info-item">
                    <label>保单号</label>
                    <span>{{ insuranceInfo.policyNo || '-' }}</span>
                </div>
                <div class="info-item">
                    <label>到期时间</label>
                    <span>{{ formatDate(insuranceInfo.expireDate) || '-' }}</span>
                </div>
                <div class="info-item">
                    <label>投保日期</label>
                    <span>{{ formatDate(insuranceInfo.startDate) || '-' }}</span>
                </div>
                <div class="info-item full-width">
                    <label>备注</label>
                    <span>{{ insuranceInfo.remark || '-' }}</span>
                </div>
            </div>
            <div v-else class="form-grid">
                <el-form-item label="保险公司">
                    <el-input v-model="insuranceForm.company"></el-input>
                </el-form-item>
                <el-form-item label="保单号">
                    <el-input v-model="insuranceForm.policyNo"></el-input>
                </el-form-item>
                <el-form-item label="到期时间">
                    <el-date-picker v-model="insuranceForm.expireDate" type="date" value-format="yyyy-MM-dd" @change="onExpireDateChange"></el-date-picker>
                </el-form-item>
                <el-form-item label="投保日期">
                    <el-date-picker v-model="insuranceForm.startDate" type="date" value-format="yyyy-MM-dd" disabled></el-date-picker>
                </el-form-item>
                <el-form-item label="备注" class="full-width">
                    <el-input v-model="insuranceForm.remark" type="textarea" :rows="2"></el-input>
                </el-form-item>
                <div class="form-actions">
                    <el-button type="primary" @click="saveInsuranceInfo">保存</el-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'

const vehicleId = ref('')
const vehicleInfo = ref({})
const checkInfo = ref({})
const insuranceInfo = ref({})

const isVehicleEditing = ref(false)
const isCheckEditing = ref(false)
const isInsuranceEditing = ref(false)

const auditFailDialogVisible = ref(false)

const auditForm = ref({
    status: '',
    remark: ''
})

const vehicleForm = ref({
    userName: '',
    plateNumber: '',
    vehicleType: '',
    useNature: '',
    registerDate: '',
    status: '',
    checkCycle: '',
    licenseImage: ''
})

const checkForm = ref({
    checkExpireDate: '',
    checkType: '',
    checkStatus: '',
    orderId: '',
    windowNo: '',
    remark: ''
})

const insuranceForm = ref({
    company: '',
    policyNo: '',
    startDate: '',
    expireDate: '',
    remark: ''
})

const getUrlParam = (name) => {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get(name)
}

const loadDetail = () => {
    vehicleId.value = getUrlParam('vehicleId')
    if (!vehicleId.value) return

    fetch(`/wxapi/getVehicleAuditDetail?vehicleId=${vehicleId.value}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'token': sessionStorage.getItem('token')
        }
    }).then(res => res.json()).then(res => {
        if (res.code === 200) {
            vehicleInfo.value = res.data || {}
            checkInfo.value = res.data?.checkInfo || {}
            insuranceInfo.value = res.data?.insuranceInfo || {}
            auditForm.value.status = vehicleInfo.value.status === '1' ? '2' : ''
        }
    })
}

const goBack = () => {
    window.history.back()
}

const getStatusTagType = (status) => {
    const map = {
        '1': 'warning',
        '2': 'success',
        '3': 'danger'
    }
    return map[status] || 'info'
}

const formatDate = (dateStr) => {
    if (!dateStr) return ''
    return dateStr
}

const getLicenseImageUrl = (imageName) => {
    if (!imageName) return ''
    return `/license/${imageName}`
}

const previewLicenseImage = () => {
    if (vehicleInfo.value.licenseImage) {
        window.open(getLicenseImageUrl(vehicleInfo.value.licenseImage), '_blank')
    }
}

const toggleVehicleEdit = () => {
    vehicleForm.value = {
        userName: vehicleInfo.value.userName || '',
        plateNumber: vehicleInfo.value.plateNumber || '',
        vehicleType: vehicleInfo.value.vehicleType || '',
        useNature: vehicleInfo.value.useNature || '',
        registerDate: vehicleInfo.value.registerDate || '',
        status: vehicleInfo.value.status || '',
        checkCycle: vehicleInfo.value.checkCycle || '',
        licenseImage: vehicleInfo.value.licenseImage || ''
    }
    isVehicleEditing.value = true
}

const cancelVehicleEdit = () => {
    isVehicleEditing.value = false
}

const saveVehicleInfo = () => {
    fetch('/wxapi/updateVehicleByAdmin', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'token': sessionStorage.getItem('token')
        },
        body: JSON.stringify({
            vehicleId: vehicleId.value,
            ...vehicleForm.value
        })
    }).then(res => res.json()).then(res => {
        if (res.code === 200) {
            alert('保存成功')
            isVehicleEditing.value = false
            loadDetail()
        } else {
            alert(res.msg || '保存失败')
        }
    })
}

const handleAuditPass = async () => {
    if (!checkInfo.value.checkExpireDate) {
        alert('审核通过需填写年检信息：检验到期日')
        return
    }
    if (!checkInfo.value.checkType) {
        alert('审核通过需填写年检信息：检车类型')
        return
    }

    try {
        const res = await fetch('/wxapi/auditVehicle', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'token': sessionStorage.getItem('token')
            },
            body: JSON.stringify({
                vehicleId: vehicleId.value,
                status: '2',
                remark: ''
            })
        })
        const data = await res.json()
        if (data.code === 200) {
            alert('审核通过')
            loadDetail()
        } else {
            alert(data.msg || '审核失败')
        }
    } catch (error) {
        alert('审核失败')
    }
}

const openAuditFailDialog = () => {
    auditForm.value.remark = vehicleInfo.value.remark || '车辆信息错误'
    auditFailDialogVisible.value = true
}

const confirmAuditFail = async () => {
    if (!auditForm.value.remark.trim()) {
        alert('请填写审核失败原因')
        return
    }

    try {
        const res = await fetch('/wxapi/auditVehicle', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'token': sessionStorage.getItem('token')
            },
            body: JSON.stringify({
                vehicleId: vehicleId.value,
                status: '3',
                remark: auditForm.value.remark
            })
        })
        const data = await res.json()
        if (data.code === 200) {
            alert('审核失败已记录')
            auditFailDialogVisible.value = false
            loadDetail()
        } else {
            alert(data.msg || '操作失败')
        }
    } catch (error) {
        alert('操作失败')
    }
}

const toggleCheckEdit = () => {
    if (!isCheckEditing.value) {
        checkForm.value = {
            checkExpireDate: checkInfo.value.checkExpireDate || '',
            checkType: checkInfo.value.checkType || '',
            checkStatus: checkInfo.value.checkStatus || '',
            orderId: checkInfo.value.orderId || '',
            windowNo: checkInfo.value.windowNo || '',
            remark: checkInfo.value.remark || ''
        }
    }
    isCheckEditing.value = !isCheckEditing.value
}

const saveCheckInfo = () => {
    fetch('/wxapi/saveCheckInfo', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'token': sessionStorage.getItem('token')
        },
        body: JSON.stringify({
            vehicleId: vehicleId.value,
            ...checkForm.value
        })
    }).then(res => res.json()).then(res => {
        if (res.code === 200) {
            alert('保存成功')
            isCheckEditing.value = false
            loadDetail()
        } else {
            alert(res.msg || '保存失败')
        }
    })
}

const onExpireDateChange = (val) => {
    if (val) {
        const expireDate = new Date(val)
        expireDate.setFullYear(expireDate.getFullYear() - 1)
        const year = expireDate.getFullYear()
        const month = String(expireDate.getMonth() + 1).padStart(2, '0')
        const day = String(expireDate.getDate()).padStart(2, '0')
        insuranceForm.value.startDate = `${year}-${month}-${day}`
    } else {
        insuranceForm.value.startDate = ''
    }
}

const toggleInsuranceEdit = () => {
    if (!isInsuranceEditing.value) {
        insuranceForm.value = {
            company: insuranceInfo.value.company || '',
            policyNo: insuranceInfo.value.policyNo || '',
            startDate: insuranceInfo.value.startDate || '',
            expireDate: insuranceInfo.value.expireDate || '',
            remark: insuranceInfo.value.remark || ''
        }
    }
    isInsuranceEditing.value = !isInsuranceEditing.value
}

const saveInsuranceInfo = () => {
    fetch('/wxapi/saveInsuranceInfo', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'token': sessionStorage.getItem('token')
        },
        body: JSON.stringify({
            vehicleId: vehicleId.value,
            ...insuranceForm.value
        })
    }).then(res => res.json()).then(res => {
        if (res.code === 200) {
            alert('保存成功')
            isInsuranceEditing.value = false
            loadDetail()
        } else {
            alert(res.msg || '保存失败')
        }
    })
}

onMounted(() => {
    loadDetail()
})
</script>

<style scoped>
.audit-detail-container {
    padding: 20px;
    background: #fff;
    min-height: calc(100vh - 80px);
}

.page-header {
    display: flex;
    align-items: center;
    gap: 15px;
    margin-bottom: 20px;
}

.page-title {
    font-size: 20px;
    font-weight: bold;
    color: #333;
    margin: 0;
}

.vehicle-info-card,
.audit-action-card,
.check-info-card,
.insurance-info-card {
    border: 1px solid #e6e6e6;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 20px;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
}

.card-actions {
    display: flex;
    gap: 10px;
    align-items: center;
}

.card-title {
    font-size: 16px;
    font-weight: bold;
    color: #333;
    margin: 0;
}

.info-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 15px;
}

.info-item {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.info-item label {
    font-size: 14px;
    color: #999;
}

.info-item span {
    font-size: 14px;
    color: #333;
}

.info-item.full-width {
    grid-column: span 4;
}

.license-image img {
    max-width: 300px;
    max-height: 200px;
    border: 1px solid #e6e6e6;
    border-radius: 4px;
    cursor: pointer;
}

.license-image-edit {
    display: flex;
    align-items: center;
    gap: 10px;
}

.status-selector {
    display: flex;
    align-items: center;
    gap: 15px;
    margin-bottom: 15px;
}

.status-selector label {
    font-size: 14px;
    font-weight: bold;
}

.remark-input {
    margin-bottom: 20px;
}

.action-buttons {
    display: flex;
    gap: 10px;
}

.form-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 15px;
}

.form-grid .full-width {
    grid-column: span 4;
}

.form-actions {
    grid-column: span 4;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding-top: 10px;
}
</style>
